import {
  Agreement,
  AgreementType,
  ApplicationProductTypeEnum,
  Mutation,
  Query,
  StartNewApplicationError,
  StartNewApplicationSuccess,
  SubmitApplicationError,
  SubmitApplicationSuccess,
  Typenames,
} from '@varo/graphql-schema-types';
import backgroundTextureSunset from 'assets/images/vxl/component/productIntroScreen/background-texture-sunset.png';
import { NativeStackHeaderProps, useNavigation, useRouter } from 'expo-router';
import { useSavingsRateInfo } from 'features/accounts/hooks/useSavingsRateInfo';
import { useSubmitApplication } from 'features/accounts/hooks/useSubmitApplication';
import WithHoldDialogueContent from 'features/accounts/savings/OpenSavingsAccount/WithHoldDialogueContent';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import StartNewApplication from 'services/graphql/mutations/StartNewApplication';
import GetAgreements from 'services/graphql/queries/GetAgreements';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LinkButton from 'shared/components/vxl/LinkButton/LinkButton';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useMutation, useQuery } from 'urql';

const OpenSavingsAccount = () => {
  const router = useRouter();
  const navigation = useNavigation();

  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'openSavings',
  });
  const [modalVisible, setModalVisible] = useState(false);
  const handleCheckBoxClickPressAnalytics = usePressableAnalytics({
    analyticsId: 'openVaroSavingTermAccepted',
  });

  const handleApplicationSubmittedAnalytics = usePressableAnalytics({
    analyticsId: 'applicationSubmitted',
  });

  const handleApplicationApprovedAnalytics = usePressableAnalytics({
    analyticsId: 'openSavingsApproved',
  });

  const [mutationResult, executeMutation] =
    useMutation<Mutation>(StartNewApplication);

  useEffect(() => {
    navigation.setOptions({
      headerTransparent: true,
      headerShown: true,
      headerBackVisible: true,
      header: (props: NativeStackHeaderProps) => (
        <ExpoNavigationBar
          {...props}
          transparent
          primaryOnPress={() => router.back()}
        />
      ),
    });
  }, [navigation, router]);

  const {
    fetching: mutationFetching,
    data: mutationData,
    error: mutationError,
  } = mutationResult;
  const newApplicationResponse =
    mutationData?.startNewApplication as StartNewApplicationError;

  const { submitApplication, submitApplicationResult } = useSubmitApplication();
  const SubmitResponseError =
    submitApplicationResult.data as SubmitApplicationError;

  const [homeTiles, setHomeTiles] = useState<string[]>([]);

  const {
    refreshTiles,
    fetching: refreshTilesFetching,
    data: refreshTilesData = {},
    error: refreshTilesError,
  } = useRefreshHomeTiles(homeTiles);

  useEffect(() => {
    if (!mutationFetching && !mutationData) {
      void executeMutation({
        idempotencyId: getUuid(),
        products: [ApplicationProductTypeEnum.Savings],
      });
    }
  }, [mutationFetching, mutationData, mutationError, executeMutation]);

  const [result] = useQuery<Query>({
    query: GetAgreements,
    requestPolicy: 'cache-and-network',
    variables: {
      types: [ApplicationProductTypeEnum.Savings],
    },
  });
  const { data: agreementsData, error: agreementsError } = result;

  const {
    rates: savingsRates,
    fetching: savingsRatesFetching,
    isUnavailable: areSavingsRatesUnavailable,
    error: savingsRatesError,
    refetch: refetchSavingsRates,
  } = useSavingsRateInfo();
  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();
  const formattedSavingsRatesError =
    useFormattedCombinedError(savingsRatesError);

  const [userAgreementChecked, setChecked] = useState(false);
  const savingAgreementData = agreementsData?.agreements?.find(
    (agreement) => agreement.type === AgreementType.SavingsAccount,
  );

  const openSavingAPI = async () => {
    const applicationResponse =
      mutationData?.startNewApplication as StartNewApplicationSuccess;
    const result = await submitApplication(
      getUuid(),
      agreementsData?.agreements?.map((agreement: Agreement) => agreement.id) ||
        [],
      applicationResponse?.application?.id || '',
    );
    handleApplicationSubmittedAnalytics();

    const submitResponseSuccess = result as SubmitApplicationSuccess;

    setHomeTiles(submitResponseSuccess.tileIdsToRefresh as string[]);
  };

  useEffect(() => {
    if (homeTiles?.length > 0) {
      refreshTiles(homeTiles);
    }
  }, [homeTiles, refreshTiles]);

  useEffect(() => {
    if (
      submitApplicationResult?.data?.__typename ===
        Typenames.SubmitApplicationSuccess &&
      Object.keys(refreshTilesData).length > 0
    ) {
      handleApplicationApprovedAnalytics();
      router.replace('/open-savings/open-savings-success');
    }
  }, [
    handleApplicationApprovedAnalytics,
    homeTiles,
    refreshTilesData,
    submitApplicationResult?.data?.__typename,
    router,
  ]);

  const handleClose = () => {
    router.push('/');
  };

  const savingsRatesErrorComponent = areSavingsRatesUnavailable ? (
    <ErrorScreen
      screenType={ErrorScreenType.Error}
      messageHeadline={formattedSavingsRatesError?.title ?? defaultTitle}
      messageBody={formattedSavingsRatesError?.message ?? defaultMessage}
      primaryButton={{
        title: retryButtonTitle,
        onPress: refetchSavingsRates,
      }}
      secondaryButton={{
        title: cancelButtonTitle,
        onPress: handleClose,
      }}
    />
  ) : null;

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={
          submitApplicationResult?.fetching ||
          mutationFetching ||
          refreshTilesFetching ||
          (savingsRatesFetching && !savingsRates)
        }
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          newApplicationResponse?.title ||
          mutationError?.name ||
          SubmitResponseError?.title ||
          agreementsError?.message ||
          refreshTilesError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={
                newApplicationResponse?.title ||
                mutationError?.name ||
                SubmitResponseError?.title
              }
              messageBody={
                newApplicationResponse?.message ||
                mutationError?.message ||
                agreementsError?.message ||
                SubmitResponseError?.message
              }
              primaryButton={{
                title: translatedText('close'),
                onPress: handleClose,
              }}
            />
          ) : (
            savingsRatesErrorComponent
          )
        }
      />
      {savingsRates ? (
        <ProductIntroScreen
          backgroundTexture={backgroundTextureSunset as ImageSourcePropType}
          visual="MONEYBAG_A"
          headlineTextStyle="expressive"
          headlineText={translatedText('openSavingsTitle')}
          topPadding={70}
          bodyContent={
            <View style={styles.wrapper}>
              <View style={styles.content}>
                <ListItem
                  primaryCopy={translatedText('listItems.title1', {
                    savingsAPY: ` ${savingsRates.standardApy}`,
                  })}
                  secondaryCopy={translatedText('listItems.value1')}
                  showsChevron={false}
                  leadingAccessory={{
                    id: 'apyIcon',
                    name: VXLComponentId.ICON_AVATAR,
                    props: {
                      iconKey: 'APY',
                    },
                  }}
                  primaryNumberOfLines={0}
                  secondaryNumberOfLines={0}
                />
                <View style={styles.hairLineDivider}>
                  <HairLineDivider />
                </View>
                <ListItem
                  primaryCopy={translatedText('listItems.title2')}
                  secondaryCopy={translatedText('listItems.value2')}
                  showsChevron={false}
                  leadingAccessory={{
                    id: 'piggyBankIcon',
                    name: VXLComponentId.ICON_AVATAR,
                    props: {
                      iconKey: 'PIGGY_BANK',
                    },
                  }}
                  primaryNumberOfLines={0}
                  secondaryNumberOfLines={0}
                />
                <HairLineDivider />
                <ListItem
                  primaryCopy={translatedText('listItems.title3')}
                  showsChevron={false}
                  leadingAccessory={{
                    id: 'fdicIcon',
                    name: VXLComponentId.ICON_AVATAR,
                    props: {
                      iconKey: 'FDIC',
                    },
                  }}
                  primaryNumberOfLines={0}
                  secondaryNumberOfLines={0}
                />
                <View style={styles.disclosure}>
                  <BodySmText numberOfLines={0}>
                    {translatedText('disclosure', { ...savingsRates })}
                  </BodySmText>
                  <BodySmText numberOfLines={0} style={styles.termsContent}>
                    {translatedText('qualifyingDepositDisclosure')}
                  </BodySmText>
                  <LinkButton
                    text={translatedText('fullTerms')}
                    size="small"
                    linkOnPress={() =>
                      router.push({
                        pathname: '/open-savings/saving-agreement',
                        params: {
                          agreementTitle: savingAgreementData?.title,
                          pdfUrl:
                            savingAgreementData?.resource?.pdfDocument?.url,
                        },
                      })
                    }
                  />
                </View>
              </View>
              <View style={styles.checkboxTextContainer}>
                <View style={styles.checkboxContainer}>
                  <Checkbox
                    labelText=""
                    checked={userAgreementChecked}
                    onSelectedStateChange={() => {
                      setChecked(!userAgreementChecked);
                      if (!userAgreementChecked) {
                        handleCheckBoxClickPressAnalytics();
                      }
                    }}
                    testId="checkBoxClicked"
                  />
                  <View style={styles.withHoldText}>
                    <BodyBaseText numberOfLines={1}>
                      {translatedText('notSubjectToText')}
                    </BodyBaseText>
                    <View testID="backupLink">
                      <LinkButton
                        text={translatedText('backupWithHold')}
                        size="base"
                        textAlignment="center"
                        linkOnPress={() => {
                          setModalVisible(true);
                        }}
                      />
                    </View>
                  </View>
                </View>
              </View>
            </View>
          }
          primaryButton={{
            label: translatedText('openSavingsButton'),
            isDisabled: !userAgreementChecked,
            isLoading:
              refreshTilesFetching || submitApplicationResult?.fetching,
            onPress: openSavingAPI,
          }}
        />
      ) : null}
      <Dialog
        isIOSCaptiveDialog
        isDialogVisible={modalVisible}
        setModalVisible={setModalVisible}
        titleText={translatedText('backupWithHold')}
        messageText={<WithHoldDialogueContent />}
        dialogButtonProps={[
          {
            label: translatedText('close'),
            onClick: () => {
              setModalVisible(false);
            },
          },
        ]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  termsContent: {
    marginVertical: theme.spacing.m,
  },
  checkboxContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    flexDirection: 'row',
    gap: theme.spacing.m,
    height: isWeb ? '50%' : null,
    width: '100%',
  },
  checkboxTextContainer: {
    padding: theme.spacing.m,
  },
  container: {
    flex: 1,
  },
  content: {
    backgroundColor: theme.color.background.primary,
    justifyContent: 'center',
    padding: theme.spacing.xs,
  },
  disclosure: {
    padding: theme.spacing.m,
  },
  hairLineDivider: { paddingHorizontal: theme.spacing.m },
  withHoldText: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
  },
  wrapper: {
    backgroundColor: theme.color.background.primary,
  },
});

export default OpenSavingsAccount;
